import { useEffect, useMemo, useState } from 'react';
import {
  CatalogProduct,
  findVariant,
  formatMoney,
  isOptionValueAvailable,
  thumbLetter,
  meaningfulOptions,
  needsVariantPicker,
  useCatalogProduct,
} from './catalog';
import { IconClose } from './icons';
import { LiveProduct } from './utils';

export type VariantPickerMode = 'add' | 'buy';

export type VariantPickerTarget = {
  product: LiveProduct;
  mode: VariantPickerMode;
};

interface Props {
  target: VariantPickerTarget | null;
  onClose: () => void;
  onConfirm: (productId: string, variantId: string, mode: VariantPickerMode) => Promise<void>;
}

/**
 * If the product needs a variant choice, open the picker; otherwise run action immediately.
 * Returns true when the picker was opened (caller should not set busy).
 */
export function maybeOpenVariantPicker(
  info: CatalogProduct | null | undefined,
  product: LiveProduct,
  mode: VariantPickerMode,
  open: (target: VariantPickerTarget) => void
): boolean {
  if (needsVariantPicker(info)) {
    open({ product, mode });
    return true;
  }
  return false;
}

export function VariantPicker({ target, onClose, onConfirm }: Props) {
  const product = target?.product || null;
  const mode = target?.mode || 'add';
  const info = useCatalogProduct(product);
  const [selections, setSelections] = useState<(string | null)[]>([null, null, null]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSelections([null, null, null]);
  }, [info?.id, target?.product.item_id]);

  const selectedVariant = useMemo(() => {
    if (!info) return null;
    return findVariant(info, selections) || info.variants[0] || null;
  }, [info, selections]);

  const options = info ? meaningfulOptions(info) : [];

  const selectOption = (optionIndex: number, value: string) => {
    if (!info) return;
    const next = [...selections] as (string | null)[];
    next[optionIndex] = value;
    setSelections(next);
  };

  if (!target || !product) return null;

  const confirm = async () => {
    if (!selectedVariant?.id || selectedVariant.available === false || busy) return;
    try {
      setBusy(true);
      await onConfirm(product.item_id, selectedVariant.id, mode);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const price =
    selectedVariant?.price != null
      ? `RM ${formatMoney(selectedVariant.price)}`
      : info?.price != null
        ? `RM ${formatMoney(info.price)}`
        : '—';

  const thumb = selectedVariant?.featuredImage || info?.image;

  return (
    <div
      className="il-variant-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Select options"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="il-variant-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="il-sheet-x" onClick={onClose} aria-label="Close">
          <IconClose size={16} />
        </button>

        <div className="il-variant-head">
          <div className="il-variant-thumb">
            {thumb ? (
              <img src={thumb} alt="" />
            ) : (
              <span className="il-bag-ph">{thumbLetter(info?.title || product.item_name)}</span>
            )}
          </div>
          <div className="il-variant-head-meta">
            <div className="il-variant-title">{info?.title || product.item_name || product.item_id}</div>
            <div className="il-variant-price">{price}</div>
          </div>
        </div>

        {options.map((opt, optionIndex) => {
          const realIndex = info!.options.indexOf(opt);
          const idx = realIndex >= 0 ? realIndex : optionIndex;
          return (
            <div key={opt.name} className="il-option-block">
              <div className="il-option-label">
                {opt.name}
                {selections[idx] ? (
                  <span className="il-option-picked">: {selections[idx]}</span>
                ) : null}
              </div>
              <div className="il-option-pills">
                {opt.values.map((val) => {
                  const available = info
                    ? isOptionValueAvailable(info, idx, val, selections)
                    : false;
                  const active = selections[idx] === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      className={`il-option-pill${active ? ' is-active' : ''}${
                        !available ? ' is-disabled' : ''
                      }`}
                      onClick={() => selectOption(idx, val)}
                    >
                      {val}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        <button
          type="button"
          className={mode === 'buy' ? 'il-btn-buy' : 'il-btn-outline il-variant-atc'}
          disabled={busy || !selectedVariant?.id || selectedVariant.available === false}
          onClick={confirm}
        >
          {busy ? (
            <>
              <span
                className={`il-btn-spinner${mode === 'add' ? ' il-btn-spinner-dark' : ''}`}
                aria-hidden
              />
              {mode === 'buy' ? 'Buying…' : 'Adding…'}
            </>
          ) : mode === 'buy' ? (
            'Buy now'
          ) : (
            'Add to cart'
          )}
        </button>
      </div>
    </div>
  );
}
